import React from "react"
import {useTranslation} from "react-i18next"

import {GenericErrorAlert} from "../../../components/Alert"
import {Loading} from "../../../components/Loading"
import {TReachRange, useReachActivitiesQuery} from "../../../queries/reachEngine"
import {AChannelsStatusEnum} from "../../../services/types.generated"
import {
  buildChannelRows,
  CHANNEL_COLORS,
  CHANNEL_ICONS,
  sumChannels,
  TReachChannelRow,
  useChannelLabels,
} from "../channels"
import {DashboardCard} from "./DashboardCard"

const barHeight = (activities: number, max: number) => (max > 0 ? `${(activities / max) * 100}%` : "0%")

const NotInFlowsSlot: React.FC = () => {
  const {t} = useTranslation()

  return (
    <div
      data-placeholder={"not-in-flows"}
      className={
        "flex h-[70px] items-center justify-center rounded-t-lg border-[1.4px] border-b-0 border-dashed border-cr-slate-25 bg-[repeating-linear-gradient(135deg,var(--color-cr-slate-1)_0_6px,var(--color-cr-slate-3)_6px_12px)] px-2 text-center"
      }
    >
      <span className={"text-[9.5px] leading-[1.4] font-bold tracking-[0.6px] text-cr-slate-50 uppercase"}>
        {t("ReachEngine_ChannelStatus_NotInFlows")}
      </span>
    </div>
  )
}

const ChannelColumn: React.FC<{row: TReachChannelRow; max: number}> = ({row, max}) => {
  const {t} = useTranslation()
  const labels = useChannelLabels()
  const Icon = CHANNEL_ICONS[row.channel]
  const inFlows = row.status !== AChannelsStatusEnum.NotInFlows

  return (
    <li data-channel={row.channel} className={"flex min-w-0 flex-1 flex-col"}>
      {/* The bar area excludes the top padding, so the tallest bar fills it and its value rides up into the headroom. */}
      <div className={"h-48 pt-6"}>
        <div data-bar-area className={"flex h-full flex-col justify-end"}>
          {inFlows ? (
            <>
              <span className={"mb-[6px] shrink-0 text-center text-[12.5px] font-bold text-cr-slate-90 tabular-nums"}>
                {row.activities.toLocaleString()}
              </span>
              <div
                aria-hidden
                data-bar={row.channel}
                className={"min-h-0.5 w-full shrink-0 rounded-t-lg"}
                style={{height: barHeight(row.activities, max), backgroundColor: CHANNEL_COLORS[row.channel]}}
              />
            </>
          ) : (
            <NotInFlowsSlot />
          )}
        </div>
      </div>
      <div
        className={
          "flex items-center justify-center gap-1.5 border-t border-cr-slate-2 pt-[9px] text-center text-[11.5px] leading-[17px] font-semibold text-cr-slate-60"
        }
      >
        <Icon aria-hidden className={"size-4 shrink-0 text-cr-slate-60"} />
        <span className={"min-w-0"}>{labels[row.channel]}</span>
      </div>
      {row.status === AChannelsStatusEnum.ActiveNoActivity && (
        <p className={"mt-1 text-center text-[11px] text-cr-slate-60"}>
          {t("ReachEngine_SalesFlowChannels_NoActivity")}
        </p>
      )}
    </li>
  )
}

const SalesFlowChannelsChart: React.FC<{rows: readonly TReachChannelRow[]}> = ({rows}) => {
  const {t} = useTranslation()
  const max = Math.max(
    0,
    ...rows.filter(row => row.status !== AChannelsStatusEnum.NotInFlows).map(row => row.activities)
  )

  return (
    <div
      role={"region"}
      aria-label={t("ReachEngine_SalesFlowChannels_Title")}
      tabIndex={0}
      className={
        "overflow-x-auto rounded-lg focus-visible:ring-2 focus-visible:ring-cr-blue focus-visible:outline-hidden"
      }
      data-testid={"sales-flow-channels-chart"}
    >
      <ul role={"list"} className={"flex min-w-[57rem] gap-[12px] lg:min-w-0"}>
        {rows.map(row => (
          <ChannelColumn key={row.channel} row={row} max={max} />
        ))}
      </ul>
    </div>
  )
}

export const SalesFlowChannelsCard: React.FC<{range: TReachRange}> = ({range}) => {
  const {t} = useTranslation()
  const {data: channels, error, isLoading, refetch} = useReachActivitiesQuery(range)
  const rows = React.useMemo(() => (channels ? buildChannelRows(channels) : undefined), [channels])
  const total = rows ? sumChannels(rows).activities : undefined

  return (
    <DashboardCard
      title={t("ReachEngine_SalesFlowChannels_Title")}
      subtitle={
        total != null
          ? t("ReachEngine_SalesFlowChannels_Subtitle", {count: total, total: total.toLocaleString()})
          : undefined
      }
    >
      <div className={"mt-[16px]"}>
        {isLoading ? (
          <div className={"h-60"}>
            <Loading size={"xl"} />
          </div>
        ) : error || !rows ? (
          <GenericErrorAlert retry={refetch} />
        ) : (
          <SalesFlowChannelsChart rows={rows} />
        )}
      </div>
    </DashboardCard>
  )
}
